// Lazy load Capacitor modules to avoid errors when not installed
let App: any = null;
let StatusBar: any = null;
let Style: any = null;
let Haptics: any = null;
let ImpactStyle: any = null;
let Keyboard: any = null;
let CapacitorCore: any = null;

async function loadCapacitorModules() {
  if (CapacitorCore !== null) {
    return CapacitorCore !== undefined;
  }
  
  try {
    CapacitorCore = await import('@capacitor/core');
    
    // Check if we're on web platform - Capacitor modules may not work properly on web
    const platform = CapacitorCore.getPlatform();
    if (platform === 'web') {
      CapacitorCore = undefined;
      return false;
    }
    
    // Only load Capacitor plugins on native platforms
    const appModule = await import('@capacitor/app');
    App = appModule.App;
    
    const statusBarModule = await import('@capacitor/status-bar');
    StatusBar = statusBarModule.StatusBar;
    Style = statusBarModule.Style;
    
    const hapticsModule = await import('@capacitor/haptics');
    Haptics = hapticsModule.Haptics;
    ImpactStyle = hapticsModule.ImpactStyle;
    
    const keyboardModule = await import('@capacitor/keyboard');
    Keyboard = keyboardModule.Keyboard;
    
    return true;
  } catch (error) {
    // Capacitor not installed or not available (web development)
    CapacitorCore = undefined;
    return false;
  }
}

/**
 * Initialize mobile-specific features
 */
export async function initMobileFeatures() {
  const isCapacitorAvailable = await loadCapacitorModules();
  
  if (!isCapacitorAvailable) {
    // Running in web browser without Capacitor - this is fine
    return;
  }

  // Check if running on native platform
  const platform = await getPlatform();
  
  if (platform !== 'web') {
    // Set status bar style
    // Note: MainActivity handles immersive mode, but we configure status bar appearance
    try {
      if (StatusBar && Style) {
        await StatusBar.setStyle({ style: Style.Dark });
        await StatusBar.setBackgroundColor({ color: '#2d5016' });
        // On Android, MainActivity will handle immersive mode
        // Status bar will be hidden by immersive mode but can be swiped to show
      }
    } catch (error) {
    }

    // Handle app state changes
    if (App) {
      App.addListener('appStateChange', ({ isActive }: { isActive: boolean }) => {
      });

      // Handle back button on Android
      App.addListener('backButton', ({ canGoBack }: { canGoBack: boolean }) => {
        if (!canGoBack) {
          App.exitApp();
        } else {
          window.history.back();
        }
      });
    }

    // Handle keyboard events
    if (Keyboard) {
      Keyboard.addListener('keyboardWillShow', (info: { keyboardHeight: number }) => {
      });

      Keyboard.addListener('keyboardWillHide', () => {
      });
    }
  }
}

/**
 * Get the current platform
 */
export async function getPlatform(): Promise<'ios' | 'android' | 'web'> {
  const isCapacitorAvailable = await loadCapacitorModules();
  
  if (!isCapacitorAvailable || !CapacitorCore) {
    return 'web';
  }
  
  return CapacitorCore.getPlatform() as 'ios' | 'android' | 'web';
}

/**
 * Trigger haptic feedback (vibration) on mobile devices
 * @param style - The impact style: 'light', 'medium', 'heavy', or 'selection'
 */
export async function triggerHaptic(style: 'light' | 'medium' | 'heavy' | 'selection' = 'medium') {
  const isCapacitorAvailable = await loadCapacitorModules();
  
  if (!isCapacitorAvailable || !Haptics || !ImpactStyle) {
    return; // Not available on web or Capacitor not loaded
  }
  
  try {
    const platform = await getPlatform();
    if (platform === 'web') {
      return;
    }
    
    switch (style) {
      case 'light':
        await Haptics.impact({ style: ImpactStyle.Light });
        break;
      case 'medium':
        await Haptics.impact({ style: ImpactStyle.Medium });
        break;
      case 'heavy':
        await Haptics.impact({ style: ImpactStyle.Heavy });
        break;
      case 'selection':
        await Haptics.selectionStart();
        break;
    }
  } catch (error) {
  }
}

